import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format as fmt, parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

/**
 * Drop-in replacement for the native HTML date input that:
 *  - Displays the chosen date as DD/MM/YYYY (the format used everywhere else
 *    in EximFlow), regardless of the user's browser locale.
 *  - Stores / emits the value as YYYY-MM-DD (compatible with every backend
 *    endpoint currently expecting an ISO date string).
 *  - Opens a shadcn calendar popover on click — works correctly inside
 *    dialogs, radio labels and form rows.
 *
 * API: <DMYDateInput value={x} onChange={(e) => setX(e.target.value)} />
 * (we emit a synthetic React-style event so existing handlers keep working).
 */
export function DMYDateInput({
  value,
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "",
  disabled = false,
  minDate,
  maxDate,
  size = "md",
  "data-testid": dataTestId,
  ...rest
}) {
  const parsed = value ? parseISO(value) : null;
  const valid = parsed && isValid(parsed);
  const heightCls =
    size === "sm" ? "h-8 text-xs" : size === "xs" ? "h-7 text-[11px]" : "h-9 text-sm";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={dataTestId}
          className={`${heightCls} w-full justify-start text-left font-normal px-2 ${className}`}
          {...rest}
        >
          <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400 shrink-0" />
          {valid ? (
            fmt(parsed, "dd/MM/yyyy")
          ) : (
            <span className="text-slate-400">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[60]" align="start">
        <Calendar
          mode="single"
          selected={valid ? parsed : undefined}
          onSelect={(d) => {
            const v = d ? fmt(d, "yyyy-MM-dd") : "";
            onChange?.({ target: { value: v } });
          }}
          disabled={(d) => {
            if (minDate) {
              const min = typeof minDate === "string" ? parseISO(minDate) : minDate;
              if (isValid(min) && d < min) return true;
            }
            if (maxDate) {
              const max = typeof maxDate === "string" ? parseISO(maxDate) : maxDate;
              if (isValid(max) && d > max) return true;
            }
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DMYDateInput;
